import { useEffect, useRef, useState, type ReactElement } from 'react';
import { BrandKit, type BrandColor, type BrandTemplate, resolveBrandColor } from '@schnitt/shared';
import { api, type BrandKitView } from '../api';
import { useEditor } from '../store';
import { useIstAdmin } from '../istAdmin';
import { Sheet, Slider } from './Sheet';

/**
 * Editing the brand kit.
 *
 * Everything here changes how *every* project looks, so the sheet shows what a
 * lower third will actually look like as you change it -- the same geometry the
 * renderer uses, at a size that fits on a phone.
 */
export function BrandKitSheet({
  onClose,
  closeLabel = 'Zurück',
  verwaltung = false,
  onAssistent,
}: {
  onClose: () => void;
  closeLabel?: string;
  /**
   * Aus der Verwaltung geöffnet (7.219; Sascha, 4. Oktober: „Mache mir noch
   * einen markenset Editor für admins“): ohne Projekt, dafür mit der Zahl der
   * Projekte, die ein Set verwenden, und mit Duplizieren und Löschen.
   */
  verwaltung?: boolean;
  /** Ein neues Markenset in vier Schritten (7.167). */
  onAssistent?: () => void;
}): ReactElement {
  const project = useEditor((s) => s.project);
  const toast = useEditor((s) => s.toast);
  const role = useEditor((s) => s.role);
  const refreshProject = useEditor((s) => s.refreshProject);

  const [kits, setKits] = useState<BrandKitView[]>([]);
  const [selected, setSelected] = useState<BrandKitView | null>(null);
  const [draft, setDraft] = useState<BrandKit | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);

  const [activeKitId, setActiveKitId] = useState<string | null>(project?.brandKitId ?? null);
  const logoInput = useRef<HTMLInputElement>(null);
  const fontInput = useRef<HTMLInputElement>(null);
  // Markensets gelten für alle Projekte; ändern dürfen sie nur Admins (7.226,
  // „Wer darf was“). Welches Set *dieses* Projekt verwendet, wählt weiter, wer
  // im Projekt mitarbeitet.
  const istAdmin = useIstAdmin();
  const editable = verwaltung || istAdmin === true;
  // Auf der Startseite gibt es die Meldungen des Schnittplatzes nicht -- dort
  // steht, was geschah, im Blatt selbst.
  const [meldung, setMeldung] = useState<string | null>(null);
  const melde: typeof toast = (art, text) => (verwaltung ? setMeldung(text) : toast(art, text));

  useEffect(() => {
    void (async () => {
      try {
        const { brandKits } = await api.brandKits();
        setKits(brandKits);
        setActiveKitId(project?.brandKitId ?? brandKits[0]?.id ?? null);
        const preferred = brandKits.find((k) => k.id === project?.brandKitId) ?? brandKits[0];
        if (preferred) {
          setSelected(preferred);
          setDraft(preferred.data);
          setName(preferred.name);
          setTemplateId(preferred.data.templates[0]?.id ?? null);
        }
      } catch (err) {
        melde('error', err instanceof Error ? err.message : 'Markensets konnten nicht geladen werden.');
      }
    })();
  }, [project?.brandKitId, toast]);

  const patch = (values: Partial<BrandKit>) => {
    if (!draft) return;
    setDraft({ ...draft, ...values });
    setDirty(true);
  };

  const patchTemplate = (id: string, changes: Partial<BrandTemplate>) => {
    if (!draft) return;
    setDraft({ ...draft, templates: draft.templates.map((t) => (t.id === id ? { ...t, ...changes } : t)) });
    setDirty(true);
  };

  const patchLayer = (templateKey: string, index: number, changes: Record<string, unknown>) => {
    if (!draft) return;
    setDraft({
      ...draft,
      templates: draft.templates.map((t) =>
        t.id === templateKey
          ? { ...t, layers: t.layers.map((l, i) => (i === index ? ({ ...l, ...changes } as typeof l) : l)) }
          : t,
      ),
    });
    setDirty(true);
  };

  const save = async () => {
    if (!selected || !draft) return;
    setBusy(true);
    try {
      const { brandKit } = await api.saveBrandKit(selected.id, name.trim() || selected.name, draft);
      // Die Zahl der Projekte kommt nur mit der Liste; sie bleibt stehen.
      setSelected((alt) => ({ ...brandKit, projekte: alt?.projekte }));
      setDraft(brandKit.data);
      setKits((prev) => prev.map((k) => (k.id === brandKit.id ? { ...brandKit, projekte: k.projekte } : k)));
      setDirty(false);
      melde('success', 'Markenset gespeichert.');
    } catch (err) {
      melde('error', err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const upload = async (kind: 'logo' | 'font', file: File | undefined) => {
    if (!selected || !file) return;
    setBusy(true);
    try {
      const { brandKit } = await api.uploadBrandFile(selected.id, kind, file);
      // Die Zahl der Projekte kommt nur mit der Liste; sie bleibt stehen.
      setSelected((alt) => ({ ...brandKit, projekte: alt?.projekte }));
      setDraft(brandKit.data);
      setKits((prev) => prev.map((k) => (k.id === brandKit.id ? { ...brandKit, projekte: k.projekte } : k)));
      melde('success', kind === 'logo' ? 'Logo übernommen.' : 'Schrift übernommen.');
    } catch (err) {
      melde('error', err instanceof Error ? err.message : 'Die Datei wurde nicht angenommen.');
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async (kind: 'logo' | 'font') => {
    if (!selected) return;
    setBusy(true);
    try {
      const { brandKit } = await api.removeBrandFile(selected.id, kind);
      setSelected((alt) => ({ ...brandKit, projekte: alt?.projekte }));
      setDraft(brandKit.data);
      melde('info', kind === 'logo' ? 'Logo entfernt.' : 'Schrift entfernt.');
    } catch (err) {
      melde('error', err instanceof Error ? err.message : 'Entfernen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const useForProject = async (kitId: string) => {
    if (!project) return;
    setBusy(true);
    try {
      await api.setProjectBrandKit(project.id, kitId);
      await refreshProject();
      setActiveKitId(kitId);
      melde('success', 'Dieses Markenset gilt jetzt für das Projekt.');
    } catch (err) {
      melde('error', err instanceof Error ? err.message : 'Wechsel fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const addKit = async () => {
    setBusy(true);
    try {
      const { brandKit } = await api.createBrandKit(`Markenset ${kits.length + 1}`);
      setKits((prev) => [...prev, brandKit]);
      setSelected(brandKit);
      setDraft(brandKit.data);
      setName(brandKit.name);
      setTemplateId(brandKit.data.templates[0]?.id ?? null);
      setDirty(false);
      melde('info', 'Markenset angelegt.');
    } catch (err) {
      melde('error', err instanceof Error ? err.message : 'Anlegen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const kopieren = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { brandKit } = await api.copyBrandKit(selected.id);
      setKits((prev) => [...prev, brandKit]);
      setSelected(brandKit);
      setDraft(brandKit.data);
      setName(brandKit.name);
      setTemplateId(brandKit.data.templates[0]?.id ?? null);
      setDirty(false);
      melde('info', `„${brandKit.name}“ angelegt.`);
    } catch (err) {
      melde('error', err instanceof Error ? err.message : 'Duplizieren ging nicht.');
    } finally {
      setBusy(false);
    }
  };

  const loeschen = async () => {
    if (!selected) return;
    if (!window.confirm(`Markenset „${selected.name}“ löschen? Logo und Schrift gehen mit.`)) return;
    setBusy(true);
    try {
      await api.deleteBrandKit(selected.id);
      const rest = kits.filter((k) => k.id !== selected.id);
      setKits(rest);
      const naechstes = rest[0] ?? null;
      setSelected(naechstes);
      setDraft(naechstes?.data ?? null);
      setName(naechstes?.name ?? '');
      setTemplateId(naechstes?.data.templates[0]?.id ?? null);
      setDirty(false);
      melde('info', `„${selected.name}“ gelöscht.`);
    } catch (err) {
      melde('error', err instanceof Error ? err.message : 'Löschen ging nicht.');
    } finally {
      setBusy(false);
    }
  };

  if (!draft || !selected) {
    return (
      <Sheet title="Markenset" onClose={onClose} closeLabel={closeLabel}>
        <p className="hint">Lade…</p>
      </Sheet>
    );
  }

  const template = draft.templates.find((t) => t.id === templateId) ?? draft.templates[0];

  return (
    <Sheet
      title="Markenset"
      onClose={onClose}
      closeLabel={closeLabel}
      actions={
        <button className="small primary" onClick={() => void save()} disabled={!dirty || busy || !editable}>
          {busy ? '…' : dirty ? 'Speichern' : 'Gespeichert'}
        </button>
      }
    >
      <div className="chips" style={{ marginBottom: 10 }}>
        {kits.map((kit) => (
          <button
            key={kit.id}
            className={`chip ${kit.id === selected.id ? 'active' : ''}`}
            data-markenset-wahl={kit.id}
            onClick={() => {
              setSelected(kit);
              setDraft(kit.data);
              setName(kit.name);
              setTemplateId(kit.data.templates[0]?.id ?? null);
              setDirty(false);
            }}
          >
            {!verwaltung && kit.id === activeKitId ? '● ' : ''}
            {kit.name}
          </button>
        ))}
        {editable ? (
          <button className="chip" onClick={() => void addKit()} disabled={busy}>
            + Neu
          </button>
        ) : null}
        {editable && onAssistent ? (
          <button className="chip" onClick={onAssistent} disabled={busy} data-markenset-assistent>
            ✨ Assistent
          </button>
        ) : null}
      </div>

      {!editable && istAdmin === false ? (
        <p className="hint" style={{ marginTop: 0 }} data-markenset-nur-lesen>
          Markensets ändern nur Admins (siehe „Wer darf was“). Welches davon dieses Projekt verwendet, wählst du hier.
        </p>
      ) : null}
      {meldung ? (
        <p className="hint" style={{ marginTop: 0 }} data-markenset-meldung>
          {meldung}
        </p>
      ) : null}
      {verwaltung ? (
        <div className="row" style={{ marginBottom: 12 }} data-markenset-verwendung={selected.projekte ?? 0}>
          <div className="grow">
            <div className="title">
              {(selected.projekte ?? 0) === 0
                ? 'Kein Projekt verwendet es'
                : selected.projekte === 1
                  ? 'Ein Projekt verwendet es'
                  : `${selected.projekte} Projekte verwenden es`}
            </div>
            <div className="sub">Änderungen wirken auf jeden neuen Export dieser Projekte.</div>
          </div>
          <button className="small" onClick={() => void kopieren()} disabled={busy} data-markenset-kopie>
            Duplizieren
          </button>
          <button
            className="small danger"
            onClick={() => void loeschen()}
            disabled={busy || (selected.projekte ?? 0) > 0 || kits.length <= 1}
            title={
              (selected.projekte ?? 0) > 0
                ? 'Erst in den Projekten ein anderes Markenset wählen'
                : kits.length <= 1
                  ? 'Das letzte Markenset bleibt'
                  : undefined
            }
            data-markenset-loeschen
          >
            Löschen
          </button>
        </div>
      ) : selected.id === activeKitId ? (
        <p className="hint" style={{ marginTop: 0 }}>
          ● Dieses Markenset verwendet das Projekt. Änderungen wirken auf jeden neuen Export.
        </p>
      ) : (
        <div className="row" style={{ marginBottom: 12 }}>
          <div className="grow">
            <div className="title">Nicht in Verwendung</div>
            <div className="sub">Das Projekt nutzt gerade ein anderes Markenset.</div>
          </div>
          <button className="small primary" onClick={() => void useForProject(selected.id)} disabled={busy || role === 'viewer'}>
            Verwenden
          </button>
        </div>
      )}

      {template ? (
        <TemplatePreview kit={draft} template={template} logoUrl={selected.logoUrl} />
      ) : null}

      <label className="field" style={{ marginTop: 14 }}>
        <span>Name</span>
        <input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setDirty(true);
          }}
          readOnly={!editable}
        />
      </label>

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 8px' }}>Farben</h3>
      <p className="hint" style={{ marginTop: 0 }}>
        Vorlagen, die auf diese Farben verweisen, ändern sich sofort mit.
      </p>
      <ColorRow label="Akzent" value={draft.primary} onChange={(v) => patch({ primary: v })} disabled={!editable} />
      <ColorRow label="Fläche" value={draft.secondary} onChange={(v) => patch({ secondary: v })} disabled={!editable} />

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Schrift</h3>
      <label className="field">
        <span>Schriftname</span>
        <input value={draft.fontFamily} onChange={(e) => patch({ fontFamily: e.target.value })} readOnly={!editable} />
      </label>
      <FileRow
        label="Schriftdatei"
        detail={draft.fontPath ? draft.fontPath.split('/').pop()! : 'Ohne — es wird die Standardschrift gesetzt'}
        accept=".ttf,.otf,font/ttf,font/otf"
        inputRef={fontInput}
        present={Boolean(draft.fontPath)}
        disabled={!editable || busy}
        onPick={(file) => void upload('font', file)}
        onRemove={() => void removeFile('font')}
        hint="TTF oder OTF, kein WOFF."
      />

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Logo</h3>
      {selected.logoUrl ? (
        <div
          style={{
            background: draft.secondary,
            borderRadius: 10,
            padding: 14,
            marginBottom: 10,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <img src={selected.logoUrl} alt="Logo" style={{ maxHeight: 70, maxWidth: '70%' }} />
        </div>
      ) : null}
      <FileRow
        label="Logodatei"
        detail={draft.logoPath ? draft.logoPath.split('/').pop()! : 'Noch keines hochgeladen'}
        accept="image/png,image/jpeg,image/webp"
        inputRef={logoInput}
        present={Boolean(draft.logoPath)}
        disabled={!editable || busy}
        onPick={(file) => void upload('logo', file)}
        onRemove={() => void removeFile('logo')}
        hint="PNG mit Transparenz, höchstens 8 MB."
      />

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Vorlagen</h3>
      <div className="chips" style={{ marginBottom: 12 }}>
        {draft.templates.map((t) => (
          <button
            key={t.id}
            className={`chip ${t.id === template?.id ? 'active' : ''}`}
            onClick={() => setTemplateId(t.id)}
          >
            {t.name}
          </button>
        ))}
      </div>

      {template ? (
        <>
          <Slider
            label="Standarddauer"
            value={template.defaultDuration}
            min={0.5}
            max={20}
            step={0.5}
            format={(v) => `${v.toFixed(1)}s`}
            onChange={(v) => patchTemplate(template.id, { defaultDuration: v })}
          />
          <div className="chips" style={{ marginBottom: 14 }}>
            {(['none', 'fade', 'slideLeft', 'slideUp'] as const).map((animation) => (
              <button
                key={animation}
                className={`chip ${template.animation === animation ? 'active' : ''}`}
                onClick={() => patchTemplate(template.id, { animation })}
                disabled={!editable}
              >
                {{ none: 'Ohne', fade: 'Blende', slideLeft: 'Von links', slideUp: 'Von unten' }[animation]}
              </button>
            ))}
          </div>

          {template.layers.map((layer, index) => (
            <div className="card" key={index} style={{ marginBottom: 10 }}>
              <div className="sub" style={{ marginBottom: 8 }}>
                {layer.type === 'rect' ? 'Fläche' : layer.type === 'logo' ? 'Logo' : `Text „${layer.slot}“`}
              </div>
              {layer.type === 'rect' ? (
                <>
                  <TokenPicker
                    value={layer.color}
                    kit={draft}
                    disabled={!editable}
                    onChange={(color) => patchLayer(template.id, index, { color })}
                  />
                  <Slider
                    label="Deckkraft"
                    value={layer.opacity}
                    min={0}
                    max={1}
                    format={(v) => `${Math.round(v * 100)}%`}
                    onChange={(v) => patchLayer(template.id, index, { opacity: v })}
                  />
                </>
              ) : null}
              {layer.type === 'text' ? (
                <>
                  <TokenPicker
                    value={layer.color ?? layer.style.color}
                    kit={draft}
                    disabled={!editable}
                    onChange={(color) => patchLayer(template.id, index, { color })}
                  />
                  <Slider
                    label="Größe"
                    value={layer.style.size}
                    min={0.015}
                    max={0.15}
                    step={0.005}
                    format={(v) => `${Math.round(v * 1000) / 10}%`}
                    onChange={(v) => patchLayer(template.id, index, { style: { ...layer.style, size: v } })}
                  />
                  <div className="chips">
                    <button
                      className={`chip ${layer.style.bold ? 'active' : ''}`}
                      onClick={() => patchLayer(template.id, index, { style: { ...layer.style, bold: !layer.style.bold } })}
                      disabled={!editable}
                    >
                      Fett
                    </button>
                    <button
                      className={`chip ${layer.style.uppercase ? 'active' : ''}`}
                      onClick={() =>
                        patchLayer(template.id, index, { style: { ...layer.style, uppercase: !layer.style.uppercase } })
                      }
                      disabled={!editable}
                    >
                      GROSS
                    </button>
                  </div>
                </>
              ) : null}
              {layer.type === 'logo' ? (
                <Slider
                  label="Breite"
                  value={layer.w}
                  min={0.03}
                  max={0.5}
                  step={0.005}
                  format={(v) => `${Math.round(v * 100)}%`}
                  onChange={(v) => patchLayer(template.id, index, { w: v })}
                />
              ) : null}
            </div>
          ))}
        </>
      ) : null}

      {dirty ? (
        <p className="hint">
          Ungespeichert. Gilt für alle Projekte mit diesem Markenset.
        </p>
      ) : null}
    </Sheet>
  );
}

function ColorRow({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}): ReactElement {
  return (
    <div className="slider-row">
      <label>{label}</label>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{ flex: 1, height: 40, padding: 2 }}
      />
      <span className="value" style={{ flexBasis: 70 }}>
        {value}
      </span>
    </div>
  );
}

/** Choose between following the kit and a colour of this layer's own. */
function TokenPicker({
  value,
  kit,
  onChange,
  disabled,
}: {
  value: BrandColor;
  kit: BrandKit;
  onChange: (value: BrandColor) => void;
  disabled: boolean;
}): ReactElement {
  const literal = value !== 'primary' && value !== 'secondary';
  return (
    <div className="chips" style={{ marginBottom: 10 }}>
      <button className={`chip ${value === 'primary' ? 'active' : ''}`} onClick={() => onChange('primary')} disabled={disabled}>
        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: kit.primary, marginRight: 6 }} />
        Akzent
      </button>
      <button
        className={`chip ${value === 'secondary' ? 'active' : ''}`}
        onClick={() => onChange('secondary')}
        disabled={disabled}
      >
        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: kit.secondary, marginRight: 6 }} />
        Fläche
      </button>
      <label className={`chip ${literal ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        Eigene
        <input
          type="color"
          value={literal ? value : '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={{ width: 28, height: 24, padding: 0, minHeight: 0, border: 'none', background: 'none' }}
        />
      </label>
    </div>
  );
}

function FileRow({
  label,
  detail,
  accept,
  inputRef,
  present,
  disabled,
  onPick,
  onRemove,
  hint,
}: {
  label: string;
  detail: string;
  accept: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  present: boolean;
  disabled: boolean;
  onPick: (file: File | undefined) => void;
  onRemove: () => void;
  hint: string;
}): ReactElement {
  return (
    <>
      <div className="row">
        <div className="grow">
          <div className="title">{label}</div>
          <div className="sub" style={{ wordBreak: 'break-all' }}>
            {detail}
          </div>
        </div>
        <button className="small" onClick={() => inputRef.current?.click()} disabled={disabled}>
          {present ? 'Ersetzen' : 'Wählen'}
        </button>
        {present ? (
          <button className="small" onClick={onRemove} disabled={disabled}>
            Weg
          </button>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(e) => {
          onPick(e.target.files?.[0]);
          // Allow picking the same file twice in a row.
          e.target.value = '';
        }}
      />
      <p className="hint">{hint}</p>
    </>
  );
}

/**
 * The template as it will be rendered, at the sequence's aspect ratio. Same
 * geometry as the renderer: positions are fractions of the frame, and text is
 * sized as a fraction of its height.
 */
export function TemplatePreview({
  kit,
  template,
  logoUrl,
  ohneSatz = false,
}: {
  kit: BrandKit;
  template: BrandTemplate;
  logoUrl: string | null;
  /** Ohne den Satz darunter -- im Assistenten stehen mehrere untereinander (7.167). */
  ohneSatz?: boolean;
}): ReactElement {
  const settings = useEditor((s) => s.timeline.settings);
  const ratio = settings.width / settings.height;

  return (
    <div>
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: ratio >= 1 ? '100%' : 200,
          aspectRatio: `${settings.width} / ${settings.height}`,
          margin: '0 auto',
          // A neutral grey stands in for footage, so a light template is
          // legible and a dark one is too.
          background: 'linear-gradient(135deg, #4b5563, #1f2937)',
          borderRadius: 10,
          overflow: 'hidden',
          containerType: 'size',
        }}
      >
        {template.layers.map((layer, index) => {
          if (layer.type === 'rect') {
            return (
              <div
                key={index}
                style={{
                  position: 'absolute',
                  left: `${layer.x * 100}%`,
                  top: `${layer.y * 100}%`,
                  width: `${layer.w * 100}%`,
                  height: `${layer.h * 100}%`,
                  background: resolveBrandColor(kit, layer.color),
                  opacity: layer.opacity,
                }}
              />
            );
          }
          if (layer.type === 'logo') {
            return logoUrl ? (
              <img
                key={index}
                src={logoUrl}
                alt=""
                style={{
                  position: 'absolute',
                  left: `${layer.x * 100}%`,
                  top: `${layer.y * 100}%`,
                  width: `${layer.w * 100}%`,
                  opacity: layer.opacity,
                }}
              />
            ) : (
              <div
                key={index}
                style={{
                  position: 'absolute',
                  left: `${layer.x * 100}%`,
                  top: `${layer.y * 100}%`,
                  width: `${layer.w * 100}%`,
                  aspectRatio: '1',
                  border: '1px dashed #ffffff66',
                  borderRadius: 4,
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 8,
                  color: '#ffffff99',
                }}
              >
                Logo
              </div>
            );
          }
          const align = layer.style.align;
          return (
            <div
              key={index}
              data-layer={index}
              style={{
                position: 'absolute',
                left: `${layer.x * 100}%`,
                top: `${layer.y * 100}%`,
                transform: align === 'center' ? 'translateX(-50%)' : align === 'right' ? 'translateX(-100%)' : undefined,
                fontSize: `${layer.style.size * 100}cqh`,
                lineHeight: 1.15,
                color: layer.color ? resolveBrandColor(kit, layer.color) : layer.style.color,
                fontWeight: layer.style.bold ? 700 : 400,
                textAlign: align,
                // The renderer draws one line and lets it run past the edge.
                // Wrapping here would show a layout that never happens.
                whiteSpace: 'pre',
              }}
            >
              {layer.style.uppercase ? layer.placeholder.toUpperCase() : layer.placeholder}
            </div>
          );
        })}
      </div>
      {ohneSatz ? null : (
        <p className="hint" style={{ textAlign: 'center' }}>
          „{template.name}“ über dem Bild. Langer Text läuft über.
        </p>
      )}
    </div>
  );
}
